/** Per-faction town layouts, GENERATED by TOWNS/tools/town_layout.py
 *  (`export_ts`) from the STANDS table. Do not edit: change the stands
 *  and re-run `town_layout.py export`. Coordinates are 1280x720 canvas
 *  pixels; `y` is a ground line, `scale` the drawn size of the slot's
 *  build width `w`. */

export interface LayoutSlot {
  readonly name: string; readonly x: number; readonly y: number;
  readonly w: number; readonly scale: number;
  /** The art's height at scale 1, measured off the raw (sprite_measure). */
  readonly h: number;
  readonly band: "back" | "middle" | "front";
  /** What the painting does not give this building to stand on. */
  readonly footing?: "bank" | "piling" | "shelf" | "plinth";
}
export interface LayoutPlot {
  readonly name: string; readonly band: "back" | "middle" | "front";
  readonly scale: number;
  readonly path: readonly (readonly [number, number])[];
}
export interface LayoutStand {
  readonly name: string; readonly band: "back" | "middle" | "front";
  readonly scale: number;
  readonly path: readonly (readonly [number, number])[];
  readonly slots: readonly string[];
}
export interface FactionLayout {
  /** Canvas rows the clouds and birds fly in. */
  readonly sky: readonly [number, number];
  readonly slots: readonly LayoutSlot[];
  readonly water: readonly (readonly [number, number])[];
  readonly lava: readonly (readonly [number, number])[];
  readonly fog: readonly (readonly [number, number, number])[];
  readonly glow: readonly (readonly [number, number, number, number, string])[];
  /** The palette the tracks are drawn in (townTracks.ts). */
  readonly ground: string;
  /** The open areas the painting offers, each with the slots standing on it. */
  readonly stands: readonly LayoutStand[];
  /** Roads between the stands, on painted ground: canvas polylines. */
  readonly roads: readonly (readonly (readonly [number, number])[])[];
  /** Painted ground with no slots on it: the prop layer may reach it. */
  readonly plots: readonly LayoutPlot[];
  /** Spans of open water, `[x0, x1, y]`, for the swell that travels along it. */
  readonly waterRuns: readonly (readonly [number, number, number])[];
}

export const TOWN_LAYOUTS: Readonly<Record<string, FactionLayout>> = Object.freeze({
  "sunspire": {
    sky: [196, 250],
    slots: [
      { name: "special_3", x: 851, y: 303, w: 140, scale: 0.415, h: 201, band: "back" },
      { name: "dwelling_2", x: 820, y: 484, w: 150, scale: 0.74, h: 164, band: "back", footing: "shelf" },
      { name: "fort", x: 470, y: 490, w: 225, scale: 1.2, h: 224, band: "back", footing: "shelf" },
      { name: "resource_silo", x: 1190, y: 494, w: 135, scale: 0.74, h: 127, band: "back", footing: "shelf" },
      { name: "dwelling_1", x: 92, y: 498, w: 120, scale: 0.82, h: 137, band: "middle" },
      { name: "grail", x: 948, y: 502, w: 180, scale: 0.74, h: 267, band: "back", footing: "shelf" },
      { name: "dwelling_5", x: 268, y: 521, w: 125, scale: 0.82, h: 192, band: "middle" },
      { name: "hall", x: 769, y: 585, w: 170, scale: 0.765, h: 188, band: "middle" },
      { name: "dwelling_3", x: 1041, y: 597, w: 125, scale: 0.814, h: 265, band: "middle" },
      { name: "dwelling_6", x: 495, y: 603, w: 130, scale: 1, h: 115, band: "middle" },
      { name: "special_2", x: 1224, y: 607, w: 115, scale: 0.98, h: 108, band: "middle" },
      { name: "mage_guild", x: 615, y: 609, w: 180, scale: 0.636, h: 423, band: "middle" },
      { name: "blacksmith", x: 710, y: 630, w: 112, scale: 1.25, h: 87, band: "front" },
      { name: "dwelling_7", x: 921, y: 682, w: 155, scale: 1.175, h: 149, band: "front" },
      { name: "marketplace", x: 1050, y: 685, w: 135, scale: 1.175, h: 115, band: "front" },
      { name: "special_1", x: 108, y: 698, w: 105, scale: 1.3, h: 57, band: "front" },
      { name: "dwelling_4", x: 262, y: 701, w: 120, scale: 1.3, h: 114, band: "front" },
      { name: "tavern", x: 1205, y: 714, w: 120, scale: 1.25, h: 73, band: "front" },
    ],
    water: [[120, 596], [168, 616], [214, 636], [262, 654], [312, 672], [366, 690], [420, 706], [150, 654], [230, 690], [300, 712], [86, 570]],
    lava: [],
    fog: [],
    glow: [],
    ground: "earth",
    stands: [
      { name: "crest", band: "back", scale: 0.5, path: [[820, 300], [1000, 292], [1180, 302]], slots: ["special_3"] },
      { name: "plateau", band: "back", scale: 0.9, path: [[300, 484], [520, 492], [700, 498]], slots: ["fort"] },
      { name: "upper", band: "back", scale: 0.74, path: [[740, 502], [1000, 494], [1240, 502]], slots: ["dwelling_2", "grail", "resource_silo"] },
      { name: "far_bank", band: "middle", scale: 0.82, path: [[30, 500], [200, 510], [380, 522]], slots: ["dwelling_1", "dwelling_5"] },
      { name: "slope", band: "middle", scale: 0.98, path: [[700, 570], [980, 592], [1280, 618]], slots: ["hall", "dwelling_3", "special_2"] },
      { name: "mill", band: "middle", scale: 1, path: [[430, 612], [680, 600]], slots: ["dwelling_6", "mage_guild"] },
      { name: "bank", band: "front", scale: 1.25, path: [[640, 622], [900, 674], [1280, 716]], slots: ["blacksmith", "dwelling_7", "marketplace", "tavern"] },
      { name: "bridgehead", band: "front", scale: 1.3, path: [[40, 708], [340, 690]], slots: ["special_1", "dwelling_4"] },
    ],
    roads: [[[300, 704], [390, 646], [444, 566], [468, 502]], [[524, 502], [664, 550], [846, 598], [1080, 652]]],
    plots: [
      { name: "plateau_open", band: "back", scale: 0.7, path: [[740, 502], [1240, 502]] },
      { name: "terrace_far", band: "back", scale: 0.62, path: [[600, 470], [860, 462], [1120, 466]] },
      { name: "meadow_open", band: "middle", scale: 0.95, path: [[480, 566], [760, 574], [1000, 582]] },
      { name: "poppies", band: "front", scale: 1.25, path: [[360, 700], [640, 716]] },
    ],
    waterRuns: [[120, 168, 606], [168, 214, 626], [150, 262, 649], [150, 312, 663], [230, 366, 685], [230, 420, 704]],
  },
  "greenmantle": {
    sky: [196, 240],
    slots: [
      { name: "special_4", x: 908, y: 323, w: 140, scale: 0.52, h: 118, band: "back" },
      { name: "special_3", x: 700, y: 327, w: 140, scale: 0.52, h: 202, band: "back" },
      { name: "dwelling_2", x: 893, y: 449, w: 150, scale: 0.705, h: 132, band: "back" },
      { name: "resource_silo", x: 722, y: 455, w: 135, scale: 0.623, h: 89, band: "back" },
      { name: "dwelling_3", x: 999, y: 486, w: 125, scale: 0.893, h: 280, band: "middle" },
      { name: "fort", x: 560, y: 513, w: 225, scale: 1.2, h: 180, band: "middle" },
      { name: "grail", x: 810, y: 513, w: 180, scale: 0.587, h: 325, band: "middle" },
      { name: "dwelling_1", x: 127, y: 540, w: 120, scale: 0.95, h: 72, band: "middle" },
      { name: "dwelling_5", x: 226, y: 562, w: 125, scale: 0.95, h: 229, band: "middle" },
      { name: "hall", x: 349, y: 564, w: 170, scale: 0.893, h: 178, band: "middle" },
      { name: "special_2", x: 1215, y: 581, w: 115, scale: 0.95, h: 105, band: "middle" },
      { name: "dwelling_6", x: 968, y: 639, w: 130, scale: 1.2, h: 121, band: "front" },
      { name: "special_1", x: 1173, y: 643, w: 105, scale: 1.128, h: 90, band: "front" },
      { name: "blacksmith", x: 104, y: 657, w: 112, scale: 1.15, h: 58, band: "front" },
      { name: "tavern", x: 1066, y: 662, w: 120, scale: 1.2, h: 104, band: "front" },
      { name: "marketplace", x: 272, y: 680, w: 135, scale: 1.081, h: 102, band: "front" },
      { name: "dwelling_7", x: 743, y: 700, w: 155, scale: 1.25, h: 140, band: "front" },
      { name: "dwelling_4", x: 403, y: 701, w: 120, scale: 1.25, h: 112, band: "front" },
      { name: "mage_guild", x: 562, y: 720, w: 180, scale: 0.697, h: 368, band: "front" },
    ],
    water: [[960, 340], [1000, 400], [980, 440], [940, 486], [900, 520], [868, 560], [830, 590], [800, 612], [1120, 240], [1125, 290], [1130, 340], [1122, 386]],
    lava: [],
    fog: [],
    glow: [],
    ground: "earth",
    stands: [
      { name: "far_clearing", band: "back", scale: 0.52, path: [[640, 318], [820, 314], [980, 320]], slots: ["special_3", "special_4"] },
      { name: "upper_meadow", band: "back", scale: 0.75, path: [[560, 430], [780, 442], [1000, 436]], slots: ["resource_silo", "dwelling_2"] },
      { name: "meadow", band: "middle", scale: 1.1, path: [[400, 506], [640, 516], [900, 506]], slots: ["fort", "grail"] },
      { name: "stream", band: "middle", scale: 0.95, path: [[940, 470], [1100, 530], [1270, 596]], slots: ["dwelling_3", "special_2"] },
      { name: "glade", band: "middle", scale: 0.95, path: [[70, 540], [250, 558], [430, 580]], slots: ["dwelling_1", "dwelling_5", "hall"] },
      { name: "hollow", band: "front", scale: 1.25, path: [[280, 698], [560, 718], [840, 700]], slots: ["dwelling_4", "mage_guild", "dwelling_7"] },
      { name: "shelf_r", band: "front", scale: 1.2, path: [[890, 636], [1080, 658], [1260, 640]], slots: ["dwelling_6", "tavern", "special_1"] },
      { name: "spur_l", band: "front", scale: 1.15, path: [[40, 658], [350, 680]], slots: ["blacksmith", "marketplace"] },
    ],
    roads: [[[560, 520], [596, 604], [572, 692]], [[430, 572], [500, 546], [560, 522]]],
    plots: [
      { name: "meadow_open", band: "middle", scale: 1, path: [[420, 546], [660, 556], [900, 546]] },
      { name: "glade_far", band: "back", scale: 0.66, path: [[330, 468], [600, 474], [860, 466]] },
      { name: "ferns", band: "front", scale: 1.25, path: [[60, 716], [640, 720], [1240, 712]] },
    ],
    waterRuns: [[960, 1130, 340], [1000, 1122, 393]],
  },
  "skyreach": {
    sky: [196, 300],
    slots: [
      { name: "special_4", x: 660, y: 336, w: 140, scale: 0.5, h: 133, band: "back" },
      { name: "grail", x: 936, y: 380, w: 180, scale: 0.62, h: 226, band: "back", footing: "shelf" },
      { name: "special_3", x: 202, y: 392, w: 140, scale: 0.62, h: 276, band: "back", footing: "shelf" },
      { name: "dwelling_2", x: 319, y: 402, w: 150, scale: 0.62, h: 170, band: "back", footing: "shelf" },
      { name: "resource_silo", x: 938, y: 406, w: 135, scale: 0.548, h: 133, band: "back", footing: "shelf" },
      { name: "special_2", x: 765, y: 483, w: 115, scale: 0.98, h: 158, band: "middle" },
      { name: "dwelling_5", x: 421, y: 490, w: 125, scale: 0.98, h: 205, band: "middle" },
      { name: "dwelling_3", x: 571, y: 505, w: 125, scale: 0.98, h: 231, band: "middle" },
      { name: "dwelling_6", x: 924, y: 510, w: 130, scale: 0.866, h: 129, band: "middle" },
      { name: "fort", x: 1120, y: 554, w: 225, scale: 1.2, h: 225, band: "middle" },
      { name: "dwelling_4", x: 164, y: 557, w: 120, scale: 0.9, h: 147, band: "middle" },
      { name: "marketplace", x: 319, y: 577, w: 135, scale: 0.9, h: 132, band: "middle" },
      { name: "hall", x: 625, y: 614, w: 170, scale: 1, h: 165, band: "middle" },
      { name: "mage_guild", x: 836, y: 622, w: 180, scale: 0.587, h: 416, band: "middle" },
      { name: "dwelling_7", x: 994, y: 628, w: 155, scale: 0.828, h: 139, band: "front" },
      { name: "tavern", x: 1205, y: 636, w: 120, scale: 0.955, h: 140, band: "front" },
      { name: "special_1", x: 106, y: 672, w: 105, scale: 1.25, h: 99, band: "front" },
      { name: "blacksmith", x: 226, y: 704, w: 112, scale: 1.25, h: 112, band: "front" },
      { name: "dwelling_1", x: 355, y: 705, w: 120, scale: 1.25, h: 112, band: "front" },
    ],
    water: [[280, 440], [300, 520], [290, 600], [250, 470], [268, 556]],
    lava: [],
    fog: [],
    glow: [],
    ground: "snow",
    stands: [
      { name: "summit", band: "back", scale: 0.5, path: [[420, 354], [660, 348], [900, 354]], slots: ["special_4"] },
      { name: "shelf_l", band: "back", scale: 0.62, path: [[100, 400], [320, 396], [520, 404]], slots: ["special_3", "dwelling_2"] },
      { name: "shelf_r", band: "back", scale: 0.62, path: [[880, 398], [1060, 392], [1240, 400]], slots: ["grail", "resource_silo"] },
      { name: "terrace", band: "middle", scale: 0.98, path: [[360, 494], [680, 502], [1000, 492]], slots: ["dwelling_5", "dwelling_3", "special_2", "dwelling_6"] },
      { name: "keep_ledge", band: "middle", scale: 1.2, path: [[990, 558], [1270, 550]], slots: ["fort"] },
      { name: "stair", band: "middle", scale: 1, path: [[540, 600], [720, 616], [900, 600]], slots: ["hall", "mage_guild"] },
      { name: "fall", band: "middle", scale: 0.9, path: [[110, 560], [380, 574]], slots: ["dwelling_4", "marketplace"] },
      { name: "snow_l", band: "front", scale: 1.25, path: [[40, 668], [240, 700], [430, 718]], slots: ["special_1", "blacksmith", "dwelling_1"] },
      { name: "snow_r", band: "front", scale: 1.15, path: [[930, 604], [1110, 632], [1270, 612]], slots: ["dwelling_7", "tavern"] },
    ],
    roads: [[[1120, 560], [982, 590], [836, 606], [724, 614]], [[680, 502], [700, 556], [716, 600]]],
    plots: [
      { name: "terrace_open", band: "middle", scale: 0.95, path: [[380, 496], [990, 494]] },
      { name: "snowfield", band: "front", scale: 1.2, path: [[440, 700], [700, 716], [930, 700]] },
      { name: "snow_right", band: "front", scale: 1.25, path: [[950, 690], [1120, 706], [1270, 698]] },
      { name: "shelf_right", band: "middle", scale: 0.9, path: [[1020, 500], [1250, 496]] },
    ],
    waterRuns: [],
  },
  "emberpit": {
    sky: [196, 250],
    slots: [
      { name: "special_3", x: 463, y: 410, w: 140, scale: 0.62, h: 190, band: "back" },
      { name: "grail", x: 866, y: 412, w: 180, scale: 0.62, h: 145, band: "back" },
      { name: "dwelling_2", x: 606, y: 414, w: 150, scale: 0.62, h: 129, band: "back" },
      { name: "resource_silo", x: 1088, y: 424, w: 135, scale: 0.62, h: 118, band: "back" },
      { name: "fort", x: 220, y: 540, w: 225, scale: 1.15, h: 190, band: "middle" },
      { name: "dwelling_6", x: 1095, y: 583, w: 130, scale: 1, h: 114, band: "middle" },
      { name: "mage_guild", x: 724, y: 585, w: 180, scale: 0.751, h: 329, band: "middle" },
      { name: "dwelling_5", x: 974, y: 587, w: 125, scale: 0.94, h: 203, band: "middle" },
      { name: "hall", x: 832, y: 592, w: 170, scale: 1, h: 172, band: "middle" },
      { name: "special_1", x: 80, y: 612, w: 105, scale: 1.15, h: 103, band: "front" },
      { name: "special_2", x: 1217, y: 613, w: 115, scale: 0.955, h: 107, band: "front" },
      { name: "blacksmith", x: 201, y: 652, w: 112, scale: 1.15, h: 110, band: "front" },
      { name: "tavern", x: 1090, y: 653, w: 120, scale: 0.955, h: 107, band: "front" },
      { name: "dwelling_1", x: 331, y: 661, w: 120, scale: 1.15, h: 103, band: "front" },
      { name: "dwelling_4", x: 981, y: 669, w: 120, scale: 1.081, h: 100, band: "front" },
      { name: "dwelling_7", x: 513, y: 699, w: 155, scale: 1.2, h: 140, band: "front" },
      { name: "dwelling_3", x: 793, y: 699, w: 125, scale: 1.06, h: 160, band: "front" },
      { name: "marketplace", x: 668, y: 718, w: 135, scale: 1.2, h: 102, band: "front" },
    ],
    water: [],
    lava: [[230, 420], [1040, 420], [430, 520], [760, 520], [150, 700], [330, 712], [520, 704], [700, 716], [880, 706], [1060, 700], [1220, 690], [640, 690]],
    fog: [],
    glow: [[640, 700, 520, 44, "255,110,30"], [230, 420, 60, 30, "255,120,40"], [1040, 420, 60, 30, "255,120,40"], [960, 540, 90, 34, "255,140,50"]],
    ground: "ash",
    stands: [
      { name: "rim_l", band: "back", scale: 0.62, path: [[420, 418], [560, 412], [700, 418]], slots: ["special_3", "dwelling_2"] },
      { name: "rim_r", band: "back", scale: 0.62, path: [[800, 420], [980, 414], [1140, 420]], slots: ["grail", "resource_silo"] },
      { name: "bench_l", band: "middle", scale: 1.05, path: [[60, 528], [220, 540], [560, 556]], slots: ["fort"] },
      { name: "bench_r", band: "middle", scale: 1, path: [[600, 586], [880, 598], [1160, 584]], slots: ["mage_guild", "hall", "dwelling_5", "dwelling_6"] },
      { name: "spur_l", band: "front", scale: 1.15, path: [[20, 604], [200, 646], [400, 678]], slots: ["special_1", "blacksmith", "dwelling_1"] },
      { name: "spur_r", band: "front", scale: 1.15, path: [[900, 668], [1120, 632], [1275, 602]], slots: ["dwelling_4", "tavern", "special_2"] },
      { name: "promontory", band: "front", scale: 1.2, path: [[420, 698], [640, 714], [880, 700]], slots: ["dwelling_7", "marketplace", "dwelling_3"] },
    ],
    roads: [[[220, 548], [212, 598], [202, 644]], [[560, 558], [700, 586], [880, 598]]],
    plots: [
      { name: "bench_open", band: "middle", scale: 1, path: [[200, 560], [640, 578], [1120, 570]] },
    ],
    waterRuns: [],
  },
  "gravewatch": {
    sky: [196, 280],
    slots: [
      { name: "resource_silo", x: 191, y: 454, w: 135, scale: 0.6, h: 115, band: "back", footing: "bank" },
      { name: "dwelling_2", x: 944, y: 454, w: 150, scale: 0.6, h: 139, band: "back", footing: "bank" },
      { name: "special_3", x: 398, y: 471, w: 140, scale: 0.6, h: 264, band: "back", footing: "bank" },
      { name: "grail", x: 1107, y: 476, w: 180, scale: 0.6, h: 222, band: "back", footing: "bank" },
      { name: "fort", x: 640, y: 508, w: 225, scale: 1.15, h: 204, band: "middle", footing: "bank" },
      { name: "dwelling_3", x: 859, y: 549, w: 125, scale: 0.94, h: 220, band: "middle", footing: "bank" },
      { name: "hall", x: 261, y: 554, w: 170, scale: 0.734, h: 177, band: "middle", footing: "bank" },
      { name: "dwelling_1", x: 464, y: 570, w: 120, scale: 0.884, h: 113, band: "middle", footing: "bank" },
      { name: "marketplace", x: 1202, y: 574, w: 135, scale: 1, h: 116, band: "middle", footing: "bank" },
      { name: "special_2", x: 1030, y: 577, w: 115, scale: 1, h: 120, band: "middle", footing: "bank" },
      { name: "dwelling_5", x: 92, y: 580, w: 125, scale: 0.94, h: 172, band: "middle", footing: "bank" },
      { name: "dwelling_6", x: 166, y: 591, w: 130, scale: 0.884, h: 125, band: "middle", footing: "bank" },
      { name: "special_1", x: 87, y: 657, w: 105, scale: 1.28, h: 70, band: "front" },
      { name: "tavern", x: 1174, y: 670, w: 120, scale: 1.131, h: 120, band: "front" },
      { name: "blacksmith", x: 247, y: 709, w: 112, scale: 1.28, h: 96, band: "front" },
      { name: "dwelling_7", x: 1058, y: 709, w: 155, scale: 1.131, h: 140, band: "front" },
      { name: "dwelling_4", x: 917, y: 716, w: 120, scale: 1.28, h: 116, band: "front" },
      { name: "mage_guild", x: 370, y: 720, w: 180, scale: 0.72, h: 378, band: "front" },
    ],
    water: [[340, 470], [500, 520], [860, 520], [1000, 510], [240, 500], [760, 494], [420, 542], [940, 546], [180, 560], [1120, 540]],
    lava: [],
    fog: [[100, 420, 260], [700, 430, 220], [300, 560, 200], [1000, 580, 220], [500, 400, 240]],
    glow: [],
    ground: "mud",
    stands: [
      { name: "far_l", band: "back", scale: 0.6, path: [[120, 472], [300, 468], [470, 474]], slots: ["resource_silo", "special_3"] },
      { name: "far_r", band: "back", scale: 0.6, path: [[820, 474], [1000, 468], [1180, 472]], slots: ["dwelling_2", "grail"] },
      { name: "gate", band: "middle", scale: 1.15, path: [[520, 508], [780, 508]], slots: ["fort"] },
      { name: "mid_l", band: "middle", scale: 1, path: [[30, 592], [280, 568], [520, 550]], slots: ["dwelling_5", "dwelling_6", "hall", "dwelling_1"] },
      { name: "mid_r", band: "middle", scale: 1, path: [[800, 550], [1040, 572], [1270, 592]], slots: ["dwelling_3", "special_2", "marketplace"] },
      { name: "near_l", band: "front", scale: 1.28, path: [[20, 646], [240, 702], [520, 722]], slots: ["special_1", "blacksmith", "mage_guild"] },
      { name: "near_r", band: "front", scale: 1.28, path: [[840, 722], [1080, 690], [1275, 648]], slots: ["dwelling_4", "dwelling_7", "tavern"] },
    ],
    roads: [[[300, 568], [520, 556]], [[1000, 566], [760, 556]]],
    plots: [
      { name: "causeway", band: "front", scale: 1.2, path: [[500, 706], [640, 718], [800, 706]] },
      { name: "flat_l", band: "middle", scale: 0.95, path: [[60, 596], [470, 556]] },
      { name: "flat_r", band: "middle", scale: 0.95, path: [[820, 556], [1260, 596]] },
    ],
    waterRuns: [[240, 340, 485], [760, 1120, 530], [940, 1120, 547]],
  },
  "deepvault": {
    sky: [196, 290],
    slots: [
      { name: "special_4", x: 1120, y: 354, w: 140, scale: 0.5, h: 116, band: "back", footing: "shelf" },
      { name: "special_3", x: 218, y: 359, w: 140, scale: 0.5, h: 284, band: "back", footing: "shelf" },
      { name: "grail", x: 958, y: 411, w: 180, scale: 0.8, h: 237, band: "middle", footing: "shelf" },
      { name: "resource_silo", x: 108, y: 413, w: 135, scale: 0.8, h: 77, band: "middle", footing: "shelf" },
      { name: "dwelling_2", x: 286, y: 425, w: 150, scale: 0.8, h: 201, band: "middle", footing: "shelf" },
      { name: "dwelling_3", x: 1204, y: 428, w: 125, scale: 0.8, h: 262, band: "middle", footing: "shelf" },
      { name: "fort", x: 460, y: 509, w: 225, scale: 1.15, h: 201, band: "middle" },
      { name: "hall", x: 757, y: 509, w: 170, scale: 1.15, h: 185, band: "middle" },
      { name: "dwelling_5", x: 119, y: 567, w: 125, scale: 0.95, h: 178, band: "middle" },
      { name: "dwelling_1", x: 1202, y: 576, w: 120, scale: 0.95, h: 107, band: "middle" },
      { name: "special_2", x: 1038, y: 581, w: 115, scale: 0.95, h: 109, band: "middle" },
      { name: "dwelling_6", x: 369, y: 583, w: 130, scale: 0.789, h: 120, band: "middle" },
      { name: "dwelling_4", x: 600, y: 656, w: 120, scale: 1.2, h: 123, band: "front" },
      { name: "dwelling_7", x: 737, y: 657, w: 155, scale: 1.2, h: 140, band: "front" },
      { name: "marketplace", x: 964, y: 694, w: 135, scale: 1.25, h: 119, band: "front" },
      { name: "special_1", x: 88, y: 700, w: 105, scale: 1.3, h: 95, band: "front" },
      { name: "tavern", x: 1195, y: 703, w: 120, scale: 1.25, h: 104, band: "front" },
      { name: "mage_guild", x: 280, y: 706, w: 180, scale: 0.72, h: 355, band: "front" },
      { name: "blacksmith", x: 203, y: 720, w: 112, scale: 1.3, h: 71, band: "front" },
    ],
    water: [[300, 520], [500, 560], [700, 500], [900, 540], [600, 600], [400, 580], [800, 570], [1000, 600], [240, 556], [1120, 540]],
    lava: [],
    fog: [],
    glow: [[250, 210, 60, 50, "170,110,255"], [640, 250, 80, 60, "170,110,255"], [1050, 230, 60, 50, "170,110,255"], [120, 600, 70, 26, "120,220,210"]],
    ground: "stone",
    stands: [
      { name: "high_l", band: "back", scale: 0.5, path: [[40, 352], [180, 362], [300, 372]], slots: ["special_3"] },
      { name: "high_r", band: "back", scale: 0.5, path: [[980, 372], [1120, 360], [1260, 350]], slots: ["special_4"] },
      { name: "ledge_l", band: "middle", scale: 0.8, path: [[40, 432], [220, 424], [420, 438]], slots: ["resource_silo", "dwelling_2"] },
      { name: "ledge_r", band: "middle", scale: 0.8, path: [[880, 420], [1060, 412], [1260, 426]], slots: ["grail", "dwelling_3"] },
      { name: "shore", band: "middle", scale: 1.15, path: [[330, 512], [640, 506], [900, 512]], slots: ["fort", "hall"] },
      { name: "terrace_l", band: "middle", scale: 0.95, path: [[60, 566], [240, 586], [420, 602]], slots: ["dwelling_5", "dwelling_6"] },
      { name: "terrace_r", band: "middle", scale: 0.95, path: [[900, 602], [1080, 582], [1270, 564]], slots: ["special_2", "dwelling_1"] },
      { name: "island", band: "front", scale: 1.2, path: [[480, 644], [660, 656], [830, 644]], slots: ["dwelling_4", "dwelling_7"] },
      { name: "floor_l", band: "front", scale: 1.3, path: [[20, 700], [220, 718], [430, 702]], slots: ["special_1", "blacksmith", "mage_guild"] },
      { name: "floor_r", band: "front", scale: 1.25, path: [[880, 692], [1080, 710], [1270, 688]], slots: ["marketplace", "tavern"] },
    ],
    roads: [[[300, 436], [200, 546], [160, 692]], [[1000, 424], [1100, 560], [1120, 690]]],
    plots: [
      { name: "shore_open", band: "middle", scale: 1.05, path: [[330, 516], [900, 516]] },
      { name: "floor_open", band: "front", scale: 1.25, path: [[400, 706], [640, 718], [880, 704]] },
    ],
    waterRuns: [[900, 1120, 540], [800, 1120, 567], [600, 1000, 587]],
  },
  "ironhorde": {
    sky: [196, 270],
    slots: [
      { name: "special_4", x: 640, y: 292, w: 140, scale: 0.45, h: 84, band: "back" },
      { name: "special_3", x: 883, y: 478, w: 140, scale: 0.62, h: 92, band: "back" },
      { name: "grail", x: 984, y: 480, w: 180, scale: 0.62, h: 265, band: "back" },
      { name: "dwelling_5", x: 1079, y: 480, w: 125, scale: 0.66, h: 191, band: "back" },
      { name: "resource_silo", x: 705, y: 483, w: 135, scale: 0.66, h: 122, band: "back" },
      { name: "dwelling_2", x: 793, y: 494, w: 150, scale: 0.62, h: 127, band: "back" },
      { name: "fort", x: 480, y: 566, w: 225, scale: 1.25, h: 203, band: "middle" },
      { name: "hall", x: 927, y: 591, w: 170, scale: 0.771, h: 165, band: "middle" },
      { name: "dwelling_6", x: 125, y: 596, w: 130, scale: 1, h: 103, band: "middle" },
      { name: "special_2", x: 1140, y: 603, w: 115, scale: 1.05, h: 109, band: "middle" },
      { name: "dwelling_3", x: 1024, y: 604, w: 125, scale: 0.771, h: 208, band: "middle" },
      { name: "mage_guild", x: 300, y: 629, w: 180, scale: 0.8, h: 355, band: "middle" },
      { name: "blacksmith", x: 253, y: 647, w: 112, scale: 1.2, h: 69, band: "front" },
      { name: "dwelling_4", x: 1032, y: 651, w: 120, scale: 0.936, h: 97, band: "front" },
      { name: "special_1", x: 83, y: 672, w: 105, scale: 1.2, h: 68, band: "front" },
      { name: "tavern", x: 1198, y: 689, w: 120, scale: 1.2, h: 106, band: "front" },
      { name: "dwelling_1", x: 842, y: 704, w: 120, scale: 1.3, h: 103, band: "front" },
      { name: "dwelling_7", x: 441, y: 720, w: 155, scale: 1.3, h: 124, band: "front" },
      { name: "marketplace", x: 653, y: 720, w: 135, scale: 1.3, h: 61, band: "front" },
    ],
    water: [],
    lava: [],
    fog: [],
    glow: [],
    ground: "earth",
    stands: [
      { name: "mesa", band: "back", scale: 0.45, path: [[380, 302], [640, 298], [900, 304]], slots: ["special_4"] },
      { name: "far_floor", band: "back", scale: 0.66, path: [[660, 488], [880, 494], [1120, 484]], slots: ["resource_silo", "dwelling_2", "special_3", "grail", "dwelling_5"] },
      { name: "floor", band: "middle", scale: 1.25, path: [[300, 560], [480, 566], [720, 556]], slots: ["fort"] },
      { name: "bench_r", band: "middle", scale: 1.05, path: [[790, 570], [1000, 596], [1200, 614]], slots: ["hall", "dwelling_3", "special_2"] },
      { name: "bench_l", band: "middle", scale: 1, path: [[60, 598], [240, 610], [420, 618]], slots: ["dwelling_6", "mage_guild"] },
      { name: "rim_l", band: "front", scale: 1.2, path: [[20, 694], [170, 656], [320, 628]], slots: ["special_1", "blacksmith"] },
      { name: "rim_r", band: "front", scale: 1.2, path: [[960, 616], [1120, 662], [1270, 702]], slots: ["dwelling_4", "tavern"] },
      { name: "rim_c", band: "front", scale: 1.3, path: [[340, 712], [640, 720], [920, 706]], slots: ["dwelling_7", "marketplace", "dwelling_1"] },
    ],
    roads: [[[480, 566], [560, 642], [640, 708]], [[880, 592], [964, 650], [1040, 700]]],
    plots: [
      { name: "floor_open", band: "middle", scale: 1.1, path: [[250, 566], [640, 560], [1100, 570]] },
      { name: "mid_open", band: "middle", scale: 0.92, path: [[640, 516], [840, 524], [1030, 530]] },
      { name: "rim_open", band: "front", scale: 1.25, path: [[300, 716], [640, 720], [980, 712]] },
    ],
    waterRuns: [],
  },
  "fenmire": {
    sky: [196, 270],
    slots: [
      { name: "grail", x: 707, y: 352, w: 180, scale: 0.55, h: 183, band: "back", footing: "bank" },
      { name: "dwelling_2", x: 408, y: 362, w: 150, scale: 0.55, h: 117, band: "back", footing: "bank" },
      { name: "special_3", x: 847, y: 362, w: 140, scale: 0.55, h: 201, band: "back", footing: "bank" },
      { name: "dwelling_6", x: 404, y: 452, w: 130, scale: 0.764, h: 113, band: "middle", footing: "bank" },
      { name: "special_2", x: 1197, y: 454, w: 115, scale: 0.92, h: 72, band: "middle", footing: "bank" },
      { name: "resource_silo", x: 118, y: 458, w: 135, scale: 0.865, h: 82, band: "middle", footing: "bank" },
      { name: "hall", x: 854, y: 458, w: 170, scale: 0.813, h: 152, band: "middle", footing: "bank" },
      { name: "dwelling_3", x: 1020, y: 474, w: 125, scale: 0.92, h: 223, band: "middle", footing: "bank" },
      { name: "dwelling_5", x: 300, y: 478, w: 125, scale: 0.813, h: 178, band: "middle", footing: "bank" },
      { name: "dwelling_1", x: 375, y: 483, w: 120, scale: 0.6624, h: 92, band: "middle", footing: "bank" },
      { name: "fort", x: 580, y: 510, w: 225, scale: 1.1, h: 186, band: "middle", footing: "bank" },
      { name: "special_1", x: 123, y: 604, w: 105, scale: 1.06, h: 36, band: "front", footing: "piling" },
      { name: "blacksmith", x: 403, y: 618, w: 112, scale: 1.2, h: 109, band: "front", footing: "piling" },
      { name: "dwelling_4", x: 902, y: 619, w: 120, scale: 1.2, h: 115, band: "front", footing: "piling" },
      { name: "marketplace", x: 1169, y: 629, w: 135, scale: 1.2, h: 95, band: "front", footing: "piling" },
      { name: "mage_guild", x: 213, y: 632, w: 180, scale: 0.72, h: 369, band: "front", footing: "piling" },
      { name: "dwelling_7", x: 601, y: 708, w: 155, scale: 1.3, h: 130, band: "front", footing: "piling" },
      { name: "tavern", x: 742, y: 716, w: 120, scale: 1.3, h: 107, band: "front", footing: "piling" },
    ],
    water: [[300, 520], [650, 500], [950, 520], [200, 680], [1000, 690], [420, 560], [760, 566], [140, 620], [1140, 600], [560, 640], [900, 680]],
    lava: [],
    fog: [[300, 400, 260], [900, 410, 220], [500, 540, 200], [140, 470, 200]],
    glow: [],
    ground: "mud",
    stands: [
      { name: "far_bank", band: "back", scale: 0.55, path: [[240, 372], [620, 362], [1000, 372]], slots: ["dwelling_2", "grail", "special_3"] },
      { name: "flat_l", band: "middle", scale: 0.92, path: [[60, 462], [300, 472], [560, 462]], slots: ["resource_silo", "dwelling_5", "dwelling_1", "dwelling_6"] },
      { name: "flat_r", band: "middle", scale: 0.92, path: [[740, 458], [980, 470], [1250, 458]], slots: ["hall", "dwelling_3", "special_2"] },
      { name: "bank", band: "middle", scale: 1.1, path: [[420, 512], [760, 508]], slots: ["fort"] },
      { name: "stilts_l", band: "front", scale: 1.2, path: [[60, 598], [260, 630], [470, 606]], slots: ["special_1", "mage_guild", "blacksmith"] },
      { name: "stilts_r", band: "front", scale: 1.2, path: [[830, 616], [1030, 642], [1250, 612]], slots: ["dwelling_4", "marketplace"] },
      { name: "boardwalk", band: "front", scale: 1.3, path: [[500, 704], [660, 720], [820, 700]], slots: ["dwelling_7", "tavern"] },
    ],
    roads: [],
    plots: [
      { name: "flat_open_l", band: "middle", scale: 0.9, path: [[60, 466], [560, 466]] },
      { name: "flat_open_r", band: "middle", scale: 0.9, path: [[740, 462], [1250, 462]] },
    ],
    waterRuns: [[300, 950, 510], [420, 760, 563], [900, 1000, 685]],
  },
  "prismgate": {
    sky: [196, 290],
    slots: [
      { name: "grail", x: 916, y: 370, w: 180, scale: 0.52, h: 238, band: "back" },
      { name: "resource_silo", x: 1073, y: 376, w: 135, scale: 0.52, h: 127, band: "back" },
      { name: "dwelling_2", x: 743, y: 379, w: 150, scale: 0.489, h: 149, band: "back" },
      { name: "dwelling_3", x: 813, y: 438, w: 125, scale: 0.85, h: 235, band: "middle" },
      { name: "dwelling_5", x: 523, y: 451, w: 125, scale: 0.85, h: 220, band: "middle" },
      { name: "hall", x: 638, y: 465, w: 170, scale: 0.751, h: 178, band: "middle" },
      { name: "dwelling_6", x: 1053, y: 468, w: 130, scale: 0.799, h: 138, band: "middle" },
      { name: "special_2", x: 750, y: 567, w: 115, scale: 0.987, h: 124, band: "middle" },
      { name: "dwelling_1", x: 996, y: 570, w: 120, scale: 1.05, h: 105, band: "middle" },
      { name: "fort", x: 340, y: 574, w: 225, scale: 1.15, h: 202, band: "middle" },
      { name: "tavern", x: 1111, y: 591, w: 120, scale: 0.928, h: 123, band: "middle" },
      { name: "mage_guild", x: 860, y: 597, w: 180, scale: 0.612, h: 395, band: "middle" },
      { name: "dwelling_7", x: 993, y: 663, w: 155, scale: 0.88, h: 135, band: "front" },
      { name: "marketplace", x: 1189, y: 666, w: 135, scale: 1.2, h: 82, band: "front" },
      { name: "special_1", x: 126, y: 687, w: 105, scale: 1.25, h: 71, band: "front" },
      { name: "dwelling_4", x: 395, y: 712, w: 120, scale: 1.25, h: 101, band: "front" },
      { name: "blacksmith", x: 256, y: 717, w: 112, scale: 1.25, h: 85, band: "front" },
    ],
    water: [[200, 500], [200, 560], [1090, 560], [1100, 620], [160, 470], [1130, 500], [240, 620], [1060, 660]],
    lava: [],
    fog: [],
    glow: [[880, 300, 180, 60, "180,120,255"], [340, 240, 140, 50, "255,230,170"]],
    ground: "earth",
    stands: [
      { name: "crown", band: "back", scale: 0.52, path: [[680, 388], [880, 378], [1120, 390]], slots: ["dwelling_2", "grail", "resource_silo"] },
      { name: "upper_l", band: "middle", scale: 0.85, path: [[470, 456], [620, 460], [770, 452]], slots: ["dwelling_5", "hall"] },
      { name: "upper_r", band: "middle", scale: 0.85, path: [[760, 452], [950, 460], [1140, 452]], slots: ["dwelling_3", "dwelling_6"] },
      { name: "terrace", band: "middle", scale: 1.15, path: [[180, 578], [340, 574], [620, 568]], slots: ["fort"] },
      { name: "bowl_r", band: "middle", scale: 1.05, path: [[690, 568], [920, 586], [1170, 572]], slots: ["special_2", "mage_guild", "dwelling_1", "tavern"] },
      { name: "near_l", band: "front", scale: 1.25, path: [[60, 684], [260, 712], [470, 722]], slots: ["special_1", "blacksmith", "dwelling_4"] },
      { name: "near_r", band: "front", scale: 1.2, path: [[900, 644], [1090, 674], [1270, 648]], slots: ["dwelling_7", "marketplace"] },
    ],
    roads: [[[340, 578], [318, 642], [300, 704]], [[620, 570], [804, 600], [952, 652]]],
    plots: [
      { name: "terrace_open", band: "middle", scale: 1.1, path: [[180, 578], [660, 566]] },
      { name: "bowl_open", band: "middle", scale: 1, path: [[700, 572], [1160, 576]] },
      { name: "plain_open", band: "middle", scale: 0.92, path: [[380, 520], [640, 528], [900, 522]] },
    ],
    waterRuns: [],
  },
  "saltreach": {
    sky: [196, 250],
    slots: [
      { name: "resource_silo", x: 813, y: 290, w: 135, scale: 0.49, h: 116, band: "back" },
      { name: "dwelling_2", x: 1024, y: 292, w: 150, scale: 0.49, h: 126, band: "back" },
      { name: "special_4", x: 1236, y: 294, w: 140, scale: 0.49, h: 125, band: "back" },
      { name: "special_5", x: 1131, y: 295, w: 140, scale: 0.49, h: 92, band: "back" },
      { name: "special_3", x: 905, y: 313, w: 140, scale: 0.407, h: 230, band: "back" },
      { name: "fort", x: 1120, y: 493, w: 225, scale: 1.2, h: 175, band: "middle" },
      { name: "dwelling_5", x: 124, y: 544, w: 125, scale: 1.03, h: 245, band: "middle" },
      { name: "dwelling_6", x: 589, y: 558, w: 130, scale: 1.03, h: 96, band: "middle" },
      { name: "mage_guild", x: 232, y: 559, w: 180, scale: 0.85, h: 357, band: "middle" },
      { name: "grail", x: 344, y: 560, w: 180, scale: 0.855, h: 315, band: "middle" },
      { name: "hall", x: 468, y: 566, w: 170, scale: 1.03, h: 171, band: "middle" },
      { name: "special_2", x: 1204, y: 606, w: 115, scale: 1.15, h: 80, band: "middle" },
      { name: "dwelling_3", x: 1052, y: 616, w: 125, scale: 0.828, h: 230, band: "middle" },
      { name: "special_1", x: 99, y: 694, w: 105, scale: 1.32, h: 76, band: "front" },
      { name: "dwelling_1", x: 421, y: 695, w: 120, scale: 1.241, h: 116, band: "front" },
      { name: "dwelling_7", x: 786, y: 696, w: 155, scale: 1.32, h: 137, band: "front" },
      { name: "tavern", x: 1111, y: 704, w: 120, scale: 1.167, h: 121, band: "front" },
      { name: "blacksmith", x: 255, y: 707, w: 112, scale: 1.32, h: 105, band: "front" },
      { name: "dwelling_4", x: 544, y: 714, w: 120, scale: 1.32, h: 120, band: "front" },
      { name: "marketplace", x: 990, y: 717, w: 135, scale: 1.241, h: 121, band: "front" },
    ],
    water: [[400, 420], [600, 380], [800, 460], [700, 520], [500, 500], [300, 560], [200, 230], [500, 236], [900, 240], [350, 470], [450, 430], [620, 440], [760, 400], [880, 500], [980, 520], [1060, 470], [250, 520], [560, 560], [700, 470], [840, 430]],
    lava: [],
    fog: [],
    glow: [],
    ground: "sand",
    stands: [
      { name: "cliff", band: "back", scale: 0.49, path: [[780, 296], [1270, 300]], slots: ["resource_silo", "special_3", "dwelling_2", "special_5", "special_4"] },
      { name: "beach", band: "middle", scale: 1.2, path: [[960, 486], [1280, 500]], slots: ["fort"] },
      { name: "headland", band: "middle", scale: 1.03, path: [[60, 548], [350, 556], [640, 566]], slots: ["dwelling_5", "mage_guild", "grail", "hall", "dwelling_6"] },
      { name: "shore", band: "middle", scale: 1.15, path: [[1000, 600], [1270, 600]], slots: ["dwelling_3", "special_2"] },
      { name: "near", band: "front", scale: 1.32, path: [[30, 700], [640, 702], [1250, 700]], slots: ["special_1", "blacksmith", "dwelling_1", "dwelling_4", "dwelling_7", "marketplace", "tavern"] },
    ],
    roads: [[[1120, 490], [1180, 560], [1150, 600]], [[350, 560], [300, 640], [250, 700]]],
    plots: [
    ],
    waterRuns: [[600, 760, 390], [400, 840, 413], [400, 840, 433], [450, 840, 453], [700, 1060, 468], [350, 1060, 485], [250, 980, 515], [300, 560, 560]],
  },
  "cogworks": {
    sky: [196, 250],
    slots: [
      { name: "special_5", x: 650, y: 292, w: 140, scale: 0.45, h: 132, band: "back" },
      { name: "hall", x: 852, y: 410, w: 170, scale: 0.85, h: 169, band: "back", footing: "shelf" },
      { name: "dwelling_2", x: 1136, y: 413, w: 150, scale: 0.85, h: 121, band: "back", footing: "shelf" },
      { name: "grail", x: 998, y: 429, w: 180, scale: 0.706, h: 291, band: "back", footing: "shelf" },
      { name: "fort", x: 270, y: 468, w: 225, scale: 1.05, h: 195, band: "middle", footing: "shelf" },
      { name: "dwelling_3", x: 829, y: 588, w: 125, scale: 1.1, h: 138, band: "middle" },
      { name: "special_2", x: 940, y: 589, w: 115, scale: 1.1, h: 91, band: "middle" },
      { name: "special_3", x: 1084, y: 600, w: 140, scale: 1.1, h: 207, band: "middle" },
      { name: "resource_silo", x: 446, y: 601, w: 135, scale: 1.1, h: 155, band: "middle" },
      { name: "dwelling_5", x: 109, y: 606, w: 125, scale: 1.1, h: 166, band: "middle" },
      { name: "dwelling_6", x: 334, y: 610, w: 130, scale: 0.914, h: 148, band: "middle" },
      { name: "special_4", x: 1193, y: 615, w: 140, scale: 1.1, h: 140, band: "middle" },
      { name: "dwelling_1", x: 520, y: 654, w: 120, scale: 1, h: 96, band: "front" },
      { name: "special_1", x: 820, y: 654, w: 105, scale: 0.94, h: 44, band: "front" },
      { name: "blacksmith", x: 116, y: 673, w: 112, scale: 1.203, h: 96, band: "front" },
      { name: "tavern", x: 1165, y: 691, w: 120, scale: 1.24, h: 100, band: "front" },
      { name: "dwelling_4", x: 392, y: 707, w: 120, scale: 0.999, h: 94, band: "front" },
      { name: "dwelling_7", x: 950, y: 713, w: 155, scale: 1.03, h: 145, band: "front" },
      { name: "mage_guild", x: 235, y: 717, w: 180, scale: 0.697, h: 373, band: "front" },
      { name: "marketplace", x: 1038, y: 717, w: 135, scale: 1.03, h: 107, band: "front" },
    ],
    water: [],
    lava: [],
    fog: [[600, 330, 200], [300, 380, 240], [1000, 372, 220]],
    glow: [],
    ground: "earth",
    stands: [
      { name: "mesa", band: "back", scale: 0.45, path: [[470, 302], [650, 298], [830, 304]], slots: ["special_5"] },
      { name: "shelf_l", band: "middle", scale: 1, path: [[100, 472], [470, 464]], slots: ["fort"] },
      { name: "shelf_r", band: "back", scale: 0.85, path: [[780, 418], [980, 412], [1200, 422]], slots: ["hall", "grail", "dwelling_2"] },
      { name: "bench_l", band: "middle", scale: 1.1, path: [[40, 628], [280, 608], [520, 592]], slots: ["dwelling_5", "dwelling_6", "resource_silo"] },
      { name: "bench_r", band: "middle", scale: 1.1, path: [[760, 588], [1020, 610], [1270, 632]], slots: ["dwelling_3", "special_2", "special_3", "special_4"] },
      { name: "railside_l", band: "front", scale: 1, path: [[450, 652], [590, 668]], slots: ["dwelling_1"] },
      { name: "railside_r", band: "front", scale: 1, path: [[750, 668], [890, 652]], slots: ["special_1"] },
      { name: "near_l", band: "front", scale: 1.28, path: [[20, 662], [240, 702], [460, 718]], slots: ["blacksmith", "mage_guild", "dwelling_4"] },
      { name: "near_r", band: "front", scale: 1.24, path: [[830, 716], [1080, 698], [1275, 658]], slots: ["dwelling_7", "marketplace", "tavern"] },
    ],
    roads: [[[270, 472], [230, 550], [200, 622]], [[1000, 420], [1060, 520], [1100, 608]]],
    plots: [
      { name: "rail_l", band: "front", scale: 1.1, path: [[420, 690], [600, 716]] },
      { name: "rail_r", band: "front", scale: 1.1, path: [[740, 716], [920, 690]] },
      { name: "bench_open_l", band: "middle", scale: 1.05, path: [[60, 624], [520, 596]] },
      { name: "bench_open_r", band: "middle", scale: 1.05, path: [[800, 596], [1250, 626]] },
    ],
    waterRuns: [],
  },
  "frostmark": {
    sky: [196, 260],
    slots: [
      { name: "special_5", x: 554, y: 289, w: 140, scale: 0.5, h: 85, band: "back" },
      { name: "special_4", x: 503, y: 366, w: 140, scale: 0.66, h: 116, band: "back", footing: "shelf" },
      { name: "special_3", x: 286, y: 370, w: 140, scale: 0.66, h: 188, band: "back", footing: "shelf" },
      { name: "dwelling_2", x: 394, y: 378, w: 150, scale: 0.66, h: 107, band: "back", footing: "shelf" },
      { name: "grail", x: 585, y: 388, w: 180, scale: 0.4752, h: 236, band: "back", footing: "shelf" },
      { name: "resource_silo", x: 117, y: 481, w: 135, scale: 0.85, h: 110, band: "middle" },
      { name: "fort", x: 740, y: 483, w: 225, scale: 1.1, h: 206, band: "middle", footing: "shelf" },
      { name: "hall", x: 1198, y: 489, w: 170, scale: 0.9, h: 141, band: "middle" },
      { name: "dwelling_5", x: 247, y: 526, w: 125, scale: 0.85, h: 168, band: "middle" },
      { name: "special_2", x: 1082, y: 532, w: 115, scale: 0.9, h: 81, band: "middle" },
      { name: "dwelling_6", x: 375, y: 540, w: 130, scale: 0.85, h: 120, band: "middle" },
      { name: "dwelling_3", x: 986, y: 543, w: 125, scale: 0.9, h: 201, band: "middle" },
      { name: "tavern", x: 831, y: 639, w: 120, scale: 1.15, h: 102, band: "front" },
      { name: "mage_guild", x: 609, y: 647, w: 180, scale: 0.795, h: 328, band: "front" },
      { name: "dwelling_4", x: 702, y: 660, w: 120, scale: 1.15, h: 108, band: "front" },
      { name: "marketplace", x: 1194, y: 687, w: 135, scale: 1.2, h: 99, band: "front" },
      { name: "special_1", x: 113, y: 688, w: 105, scale: 1.2, h: 86, band: "front" },
      { name: "dwelling_7", x: 1023, y: 691, w: 155, scale: 1.2, h: 136, band: "front" },
      { name: "dwelling_1", x: 388, y: 708, w: 120, scale: 1.2, h: 94, band: "front" },
      { name: "blacksmith", x: 246, y: 713, w: 112, scale: 1.2, h: 105, band: "front" },
    ],
    water: [[300, 620], [700, 600], [1000, 640], [500, 596], [860, 626], [620, 656], [400, 664]],
    lava: [],
    fog: [],
    glow: [],
    ground: "snow",
    stands: [
      { name: "glacier", band: "back", scale: 0.5, path: [[420, 308], [650, 302], [880, 310]], slots: ["special_5"] },
      { name: "terrace", band: "back", scale: 0.66, path: [[240, 378], [460, 370], [680, 380]], slots: ["special_3", "dwelling_2", "special_4", "grail"] },
      { name: "spur", band: "middle", scale: 1.1, path: [[610, 486], [900, 480]], slots: ["fort"] },
      { name: "slope_l", band: "middle", scale: 0.85, path: [[60, 472], [240, 518], [430, 558]], slots: ["resource_silo", "dwelling_5", "dwelling_6"] },
      { name: "slope_r", band: "middle", scale: 0.9, path: [[930, 562], [1140, 512], [1275, 472]], slots: ["dwelling_3", "special_2", "hall"] },
      { name: "ice_l", band: "front", scale: 1.2, path: [[50, 688], [260, 708], [460, 718]], slots: ["special_1", "blacksmith", "dwelling_1"] },
      { name: "ice_c", band: "front", scale: 1.15, path: [[480, 638], [700, 654], [900, 640]], slots: ["mage_guild", "dwelling_4", "tavern"] },
      { name: "ice_r", band: "front", scale: 1.2, path: [[930, 702], [1120, 692], [1275, 668]], slots: ["dwelling_7", "marketplace"] },
    ],
    roads: [[[740, 488], [824, 544], [884, 602], [900, 648]], [[300, 532], [300, 620], [320, 698]]],
    plots: [
      { name: "ice_open", band: "front", scale: 1.2, path: [[300, 700], [640, 716], [1000, 700]] },
      { name: "spur_open", band: "middle", scale: 1, path: [[610, 490], [900, 484]] },
    ],
    waterRuns: [[500, 860, 610], [860, 1000, 637], [400, 620, 660]],
  },
  "blackwater": {
    sky: [196, 260],
    slots: [
      { name: "special_3", x: 808, y: 304, w: 140, scale: 0.39, h: 224, band: "back" },
      { name: "resource_silo", x: 165, y: 432, w: 135, scale: 0.85, h: 93, band: "middle", footing: "plinth" },
      { name: "dwelling_2", x: 294, y: 436, w: 150, scale: 0.85, h: 128, band: "middle", footing: "plinth" },
      { name: "grail", x: 1164, y: 439, w: 180, scale: 0.664, h: 333, band: "middle", footing: "plinth" },
      { name: "dwelling_5", x: 467, y: 442, w: 125, scale: 0.85, h: 190, band: "middle", footing: "plinth" },
      { name: "special_2", x: 1145, y: 451, w: 115, scale: 0.85, h: 78, band: "middle", footing: "plinth" },
      { name: "dwelling_3", x: 813, y: 454, w: 125, scale: 0.85, h: 197, band: "middle", footing: "plinth" },
      { name: "fort", x: 980, y: 513, w: 225, scale: 1.2, h: 212, band: "middle", footing: "plinth" },
      { name: "dwelling_1", x: 504, y: 575, w: 120, scale: 0.94, h: 109, band: "middle" },
      { name: "hall", x: 125, y: 592, w: 170, scale: 0.94, h: 195, band: "middle" },
      { name: "dwelling_6", x: 265, y: 595, w: 130, scale: 1, h: 120, band: "middle" },
      { name: "dwelling_4", x: 682, y: 660, w: 120, scale: 1.2, h: 118, band: "front" },
      { name: "dwelling_7", x: 848, y: 692, w: 155, scale: 1.2, h: 164, band: "front" },
      { name: "marketplace", x: 1024, y: 694, w: 135, scale: 1.2, h: 117, band: "front" },
      { name: "special_1", x: 96, y: 699, w: 105, scale: 1.25, h: 87, band: "front" },
      { name: "tavern", x: 1178, y: 715, w: 120, scale: 1.2, h: 104, band: "front" },
      { name: "blacksmith", x: 241, y: 720, w: 112, scale: 1.25, h: 110, band: "front" },
      { name: "mage_guild", x: 398, y: 720, w: 180, scale: 0.781, h: 372, band: "front" },
    ],
    water: [[200, 330], [600, 330], [1000, 330], [300, 490], [900, 490], [480, 508], [700, 500], [150, 520], [1120, 512], [620, 546], [380, 552]],
    lava: [],
    fog: [[0, 340, 300], [700, 350, 260], [200, 500, 240], [900, 510, 240]],
    glow: [[400, 470, 40, 16, "80,220,190"], [1000, 480, 40, 16, "80,220,190"], [200, 600, 50, 18, "80,220,190"], [760, 620, 50, 18, "80,220,190"]],
    ground: "mud",
    stands: [
      { name: "treeline", band: "back", scale: 0.5, path: [[200, 306], [640, 302], [1080, 308]], slots: ["special_3"] },
      { name: "terrace_l", band: "middle", scale: 0.85, path: [[60, 448], [300, 440], [520, 450]], slots: ["resource_silo", "dwelling_2", "dwelling_5"] },
      { name: "terrace_r", band: "middle", scale: 0.85, path: [[760, 450], [1000, 440], [1240, 448]], slots: ["dwelling_3", "special_2", "grail"] },
      { name: "platform", band: "middle", scale: 1.2, path: [[820, 516], [1160, 510]], slots: ["fort"] },
      { name: "bank_l", band: "middle", scale: 1, path: [[40, 604], [280, 588], [560, 572]], slots: ["hall", "dwelling_6", "dwelling_1"] },
      { name: "near_l", band: "front", scale: 1.25, path: [[30, 700], [260, 718], [500, 706]], slots: ["special_1", "blacksmith", "mage_guild"] },
      { name: "near_r", band: "front", scale: 1.2, path: [[610, 658], [900, 692], [1250, 714]], slots: ["dwelling_4", "dwelling_7", "marketplace", "tavern"] },
    ],
    roads: [[[980, 514], [900, 582], [820, 642], [764, 690]]],
    plots: [
      { name: "terrace_open_l", band: "middle", scale: 0.9, path: [[60, 444], [520, 446]] },
      { name: "terrace_open_r", band: "middle", scale: 0.9, path: [[760, 446], [1240, 444]] },
      { name: "bank_open", band: "front", scale: 1.2, path: [[300, 700], [600, 714], [900, 700]] },
    ],
    waterRuns: [[700, 1120, 503], [150, 480, 514], [380, 620, 549]],
  },
});
